import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, FileText } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const empty = {
    company_name: "",
    tax_id: "",
    address: "",
    email: "",
    pec_sdi: "",
    phone: "",
};

export default function BillingInfoPage() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [form, setForm] = useState(empty);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);

    const nextPath = location.state?.next || "/pricing";

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/billing/info");
                if (data.billing_info) setForm({ ...empty, ...data.billing_info });
                if (!data.billing_info && user?.email) {
                    setForm((f) => ({ ...f, email: user.email }));
                }
            } catch (_) {}
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...form };
            if (!payload.pec_sdi.trim()) payload.pec_sdi = "0000000";
            await api.put("/billing/info", payload);
            await refresh();
            toast.success("Dati di fatturazione salvati");
            navigate(nextPath, { replace: true });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="min-h-screen bg-sf-bg"
            data-testid="billing-info-page"
        >
            <header className="glass-nav sticky top-0 z-30">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between">
                    <Link to="/app">
                        <Logo className="h-24 w-auto" />
                    </Link>
                    <Link
                        to="/app"
                        className="text-sm text-sf-secondary hover:text-navy underline"
                        data-testid="billing-info-back"
                    >
                        Torna alla dashboard
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
                <div className="flex items-start gap-4 mb-8">
                    <div className="w-12 h-12 rounded-lg bg-amber-brand text-navy flex items-center justify-center flex-shrink-0">
                        <FileText size={22} />
                    </div>
                    <div>
                        <div className="label-eyebrow mb-2">Fatturazione</div>
                        <h1 className="font-display text-4xl text-navy leading-tight">
                            Dati per la fatturazione
                        </h1>
                        <p className="text-sf-secondary mt-2 leading-relaxed">
                            Per attivare l'abbonamento a Staff Flow 360 ci servono
                            i tuoi dati fiscali. Verranno usati esclusivamente per
                            la fatturazione e potrai modificarli in qualsiasi
                            momento.
                        </p>
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    className="bg-white border border-sf rounded-2xl p-8 space-y-5"
                    data-testid="billing-info-form"
                >
                    <Field
                        label="Ragione sociale / Nome e cognome"
                        required
                        testid="bi-company"
                    >
                        <Input
                            value={form.company_name}
                            onChange={(e) =>
                                setForm({ ...form, company_name: e.target.value })
                            }
                            required
                            placeholder="Mario Rossi · Trattoria Da Mario S.r.l."
                            data-testid="bi-company-input"
                        />
                    </Field>

                    <Field
                        label="P. IVA / Codice Fiscale"
                        required
                        testid="bi-tax"
                    >
                        <Input
                            value={form.tax_id}
                            onChange={(e) =>
                                setForm({ ...form, tax_id: e.target.value })
                            }
                            required
                            placeholder="01234567890 / RSSMRA80A01H501Z"
                            data-testid="bi-tax-input"
                        />
                    </Field>

                    <Field label="Indirizzo" required testid="bi-address">
                        <Input
                            value={form.address}
                            onChange={(e) =>
                                setForm({ ...form, address: e.target.value })
                            }
                            required
                            placeholder="Via Roma 12, 00100 Roma (RM)"
                            data-testid="bi-address-input"
                        />
                    </Field>

                    <div className="grid md:grid-cols-2 gap-5">
                        <Field label="Email" required testid="bi-email">
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) =>
                                    setForm({ ...form, email: e.target.value })
                                }
                                required
                                placeholder="fatturazione@miolocale.it"
                                data-testid="bi-email-input"
                            />
                        </Field>
                        <Field label="Telefono" required testid="bi-phone">
                            <Input
                                value={form.phone}
                                onChange={(e) =>
                                    setForm({ ...form, phone: e.target.value })
                                }
                                required
                                placeholder="+39 333 1234567"
                                data-testid="bi-phone-input"
                            />
                        </Field>
                    </div>

                    <Field label="PEC o Codice SDI" required testid="bi-pec">
                        <Input
                            value={form.pec_sdi}
                            onChange={(e) =>
                                setForm({ ...form, pec_sdi: e.target.value })
                            }
                            required
                            placeholder="pec@pec.it oppure 7 caratteri SDI"
                            data-testid="bi-pec-input"
                        />
                        <div className="text-xs text-sf-muted mt-1.5">
                            In assenza di entrambi, scrivi{" "}
                            <code className="font-mono bg-[#f0efeb] px-1 rounded">
                                0000000
                            </code>
                            .
                        </div>
                    </Field>

                    <div className="pt-2">
                        <Button
                            type="submit"
                            disabled={busy || loading}
                            className="bg-navy text-white lift w-full md:w-auto"
                            data-testid="billing-info-submit"
                        >
                            {busy
                                ? "Salvataggio…"
                                : user?.has_billing_info
                                ? "Aggiorna dati"
                                : "Salva e prosegui"}
                            <ArrowRight size={16} className="ml-2" />
                        </Button>
                    </div>
                </form>

                <div className="mt-8 text-xs text-sf-muted">
                    I tuoi dati sono protetti. Staff Flow 360 è un prodotto di
                    Makai · P.IVA 09364410969.
                </div>
            </main>
        </div>
    );
}

function Field({ label, required, children, testid }) {
    return (
        <div data-testid={testid}>
            <Label className="text-xs uppercase tracking-wider text-sf-muted">
                {label}
                {required && <span className="text-amber-brand"> *</span>}
            </Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
